import { Show, Match, Switch, useContext, createMemo } from 'solid-js';
import { date, HSLColor } from 'common';
import { requireCalendarContext } from '../st-schedule-grid';
import Popover from '../../st-popover/st-popover';
import styles from '../st-schedule-grid.module.css';
import { Flexbox, Icon } from '../../..';
import { AppointmentInfo } from '../../../api/stApp';

const { truncateTime } = date;

interface AppointmentProps {
    id: string
    date: Date
    displayType: 'bullet' | 'icon' | 'details'
}

const msPerDay = 24 * 60 * 60 * 1000;
export function Appointment(props: AppointmentProps & AppointmentInfo) {
    const ctx = requireCalendarContext();
    const isStartDate = date.getDate(props.start) === props.date.getDate();
    const legendItem = ctx.categoryInfo(props.category);
    const startTime = createMemo(() => {
        return date.toLocaleTimeString(props.start, 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    });

    const colspan = Math.min(
        7 - props.date.getDay(),
        Math.max(1, ((truncateTime(props.end).getTime() - truncateTime(props.start).getTime()) / msPerDay))
    );
    const allDay = colspan > 1 || date.getTime(props.start) === truncateTime(props.start).getTime();
    const color = props.colorIndex < 0
        ? ctx.nextColor(legendItem?.key ?? props.category ?? props.id)
        : ctx.getColor(props.colorIndex);

    const isEnabled = createMemo(() => {
        const enabled = ctx.enabledLegendItems()[props.category || props.id];
        return enabled === undefined ? true : enabled;
    });

    let scheduleEl: HTMLElement;
    function initZIndex(el: HTMLElement) {
        let zIndex = '';
        el.addEventListener('mouseenter', () => {
            const cell = el.closest('.cell') as HTMLElement;
            if (cell) {
                zIndex = cell.style.zIndex;
                cell.style.zIndex = '10';
            }
        });
        el.addEventListener('mouseleave', () => {
            const cell = el.closest('.cell') as HTMLElement;
            if (cell) {
                cell.style.zIndex = zIndex;
            }
        });

        scheduleEl = el;
    }

    function showDetails() {
        if (window.ST?.OpenViewInDialog && props.viewID) {
            window.ST.OpenViewInDialog(props.viewID, props.id, {
                REV_RENDER: {
                    PASS_THROUGH: {
                        'ID': props.id,
                        'Title': props.title
                            ?.replaceAll('<', '&lt;')
                            .replaceAll('>', '&gt;'),
                        'SubTitle': props.subTitle,
                        'ViewID': props.viewID,
                        'Category': props.category,
                        'ColorIndex': props.colorIndex,
                        'Start': props.start,
                        'End': props.end,
                    }
                }
            }, scheduleEl, { fullscreen: true });
        }
    }

    return <Show when={isEnabled()}>
        <div role="button" class={styles.appointment} onClick={showDetails}>
            <Switch>
                <Match when={!props.description}>
                    <AppointmentHeading {...props} />
                </Match>
                <Match when={!!props.description}>
                    <Popover placement="bottom">
                        <Popover.Trigger>
                            <AppointmentHeading {...props} />
                        </Popover.Trigger>
                        <Popover.Content>
                            <span class={`${styles['appt-popover']} flexbox vertical auto`}>
                                <span class={styles.title}>{props.title}</span>
                                <Show when={props.subTitle}>
                                    <span class={styles.subtitle}>{props.subTitle}</span>
                                </Show>
                                <p innerHTML={props.description}></p>
                                <Show when={!allDay}>
                                    <span style="text-align: right">{startTime()}</span>
                                </Show>
                            </span>
                        </Popover.Content>
                    </Popover>
                </Match>
            </Switch>
        </div>
    </Show>
}

function AppointmentHeading(props: { id: string, date: Date } & AppointmentInfo) {
    const ctx = requireCalendarContext();
    const isStartDate = date.getDate(props.start) === props.date.getDate();
    const legendItem = ctx.categoryInfo(props.category);
    const startTime = createMemo(() => {
        return date.toLocaleTimeString(props.start, 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    });
    const colspan = Math.min(
        7 - props.date.getDay(),
        Math.max(1, ((truncateTime(props.end).getTime() - truncateTime(props.start).getTime()) / msPerDay))
    );
    const allDay = colspan > 1 || date.getTime(props.start) === truncateTime(props.start).getTime();
    const color = props.colorIndex < 0
        ? ctx.nextColor(legendItem?.key ?? props.category ?? props.id)
        : ctx.getColor(props.colorIndex);

    return <Switch>
        <Match when={allDay === false}>
            <Flexbox dir="horizontal">
                <span role="presentation"
                    class={`flexbox vertical middle ${styles.icon}`}
                    style={{
                        'background-color': color.toString(),
                        'color': color.gsComplement().toString(),
                        'border-radius': '4px',
                        'margin-right': '5px',
                    }}>
                    <Icon size={16} src={legendItem?.iconPath ?? props.iconPath ?? ''} />
                </span>
                <Flexbox as="span" dir="vertical" auto>
                    <span class={styles.title}>{props.title}</span>
                    <Show when={props.subTitle}>
                        <span class={styles.subtitle}>{props.subTitle}</span>
                    </Show>
                    <span style="text-align: right">{startTime()}</span>
                </Flexbox>
            </Flexbox>
        </Match>
        <Match when={allDay === true}>
            <Flexbox dir="horizontal" middle style={{
                'background-color': color.toString(),
                color: color.gsComplement().toString(),
                'border-radius': '4px',
            }}>
                <Show when={legendItem?.iconPath ?? props.iconPath}>
                    <span role="presentation"
                        class={`${styles.icon} flexbox vertical middle`}
                        style={{color: color.toString() }}>
                        <Icon size={16} src={legendItem?.iconPath ?? props.iconPath ?? ''} />
                    </span>
                </Show>
                <span class={`${styles.title} auto`}>{props.title}</span>
            </Flexbox>
        </Match>
    </Switch>
}

function AppointmentDetails(props: AppointmentProps & AppointmentInfo) {
    const startTime = createMemo(() => {
        return date.toLocaleTimeString(props.start, 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    });

    return <div class="auto flexbox vertical">
        <div style="white-space: normal">
            {props.title}
        </div>
        <Show when={props.subTitle}>
            <div style="white-space: normal; font-style: italic; color: var(--st-color-dim-fg)">
                {props.subTitle}
            </div>
        </Show>
        <Flexbox dir="horizontal" style="font-weight: bold">
            <span class="auto"></span>
            <span style="white-space: nowrap; padding-right: 10px">{startTime()}</span>
        </Flexbox>
    </div>
}

export function FullAppointment(appt: AppointmentInfo & { calDate: Date }) {
    const ctx = requireCalendarContext();

    const isStartDate = date.getDate(appt.start) === appt.calDate.getDate();
    const legendItem = ctx.categoryInfo(appt.category);
    const color = appt.colorIndex < 0
        ? ctx.nextColor(legendItem?.key ?? appt.category ?? appt.ID)
        : ctx.getColor(appt.colorIndex);

    const colspan = Math.min(
        7 - appt.calDate.getDay(),
        Math.max(1, ((truncateTime(appt.end).getTime() - truncateTime(appt.start).getTime()) / msPerDay))
    );
    const allDay = colspan > 1 || date.getTime(appt.start) === truncateTime(appt.start).getTime();

    const startTime = createMemo(() => {
        return date.toLocaleTimeString(appt.start, 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    });
    const showDetails = createMemo(() => !!(appt.subTitle || appt.description));

    return <div class={styles.appointment} style={{
        overflow: 'hidden',
        position: 'relative',
    }} onClick={() => ctx.showDetails(appt)} tabindex="0">
        <Flexbox dir="horizontal" middle role="presentation"
            class={styles.title}
            style={{
                'background-color': color.toString()
            }}>
            <Show when={legendItem?.iconPath ?? appt.iconPath}>
                <Flexbox as="span" dir="vertical" middle role="presentation"
                    class={styles.icon}
                    style={{ color: color.toString() }}>
                    <Icon size={16} src={legendItem?.iconPath ?? appt.iconPath ?? ''} />
                </Flexbox>
            </Show>
            <span class={`${styles.title} auto`} style={{ color: color.gsComplement().toString() }}>
                {appt.title}
            </span>
        </Flexbox>
        <Show when={showDetails()}>
            <Flexbox dir="horizontal">
                <span class={styles.gutter}>
                    <span style={{
                        'background-color': color.toString(),
                        color: color.gsComplement().toString()
                    }}>&nbsp;</span>
                </span>
                <Flexbox dir="vertical" auto style="padding: 5px; white-space: initial">
                    <span class={styles.subtitle}>{appt.subTitle}</span>
                    <span class={styles.description} innerHTML={appt.description}></span>
                    <Show when={!allDay}>
                        <span style="text-align:right">{startTime()}</span>
                    </Show>
                </Flexbox>
            </Flexbox>
        </Show>
    </div>
}
